import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, CalendarClock,
  FileText, Bell, Loader2
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCondominium } from "@/hooks/useCondominium";
import { useCondominiumStats } from "@/hooks/useCondominiumStats";
import { useFinancialRevenues } from "@/hooks/useFinancialRevenues";
import { useFinancialExpenses } from "@/hooks/useFinancialExpenses";
import { useFinancialCharges } from "@/hooks/useFinancialCharges";
import { useMeetings } from "@/hooks/useMeetings";
import { useIncidents } from "@/hooks/useIncidents";
import { useAnnouncements } from "@/hooks/useAnnouncements";
import { useAllBookings } from "@/hooks/useBookings";
import { useNavigate } from "react-router-dom";
import PublicarAvisoModal from "../PublicarAvisoModal";
import BtnSair from "../BtnSair";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from "recharts";

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);

const DashboardSindico = () => {
  const { profile } = useAuth();
  const { data: condominium } = useCondominium();
  const { data: stats, isLoading: statsLoading } = useCondominiumStats();
  const { data: revenues } = useFinancialRevenues();
  const { data: expenses } = useFinancialExpenses();
  const { data: charges } = useFinancialCharges();
  const { meetings } = useMeetings();
  const { data: incidents } = useIncidents();
  const { data: announcements } = useAnnouncements();
  const { data: bookings } = useAllBookings();
  const navigate = useNavigate();

  // Saldo em caixa
  const saldo = useMemo(() => {
    const totalRev = (revenues || []).reduce((s, r) => s + r.amount_cents, 0);
    const totalExp = (expenses || []).reduce((s, e) => s + e.amount_cents, 0);
    return totalRev - totalExp;
  }, [revenues, expenses]);

  // Inadimplência
  const inadimplencia = useMemo(() => {
    if (!charges || charges.length === 0) return { pct: 0, count: 0 };
    const overdue = charges.filter(c => c.status === "overdue" || (c.status === "pending" && new Date(c.due_date) < new Date()));
    return { pct: Math.round((overdue.length / charges.length) * 100), count: overdue.length };
  }, [charges]);

  // Chamados abertos + urgentes
  const chamados = useMemo(() => {
    const open = (incidents || []).filter(i => i.status === "open" || i.status === "in_progress");
    const urgent = open.filter(i => i.priority === "high" || i.priority === "urgent");
    return { total: open.length, urgent: urgent.length };
  }, [incidents]);

  // Próximas assembleias
  const proximasAssembleias = useMemo(() => {
    const now = new Date();
    const futuras = (meetings || []).filter(m => new Date(m.scheduled_date) > now);
    const proxima = futuras.length > 0
      ? format(new Date(futuras[0].scheduled_date), "dd/MM/yyyy", { locale: ptBR })
      : null;
    return { count: futuras.length, proxima };
  }, [meetings]);

  // Fluxo de caixa - últimos 6 meses
  const cashFlowData = useMemo(() => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(now, i);
      const start = startOfMonth(date);
      const end = endOfMonth(date);
      const label = format(date, "MMM", { locale: ptBR });

      const rev = (revenues || [])
        .filter(r => { const d = new Date(r.revenue_date); return d >= start && d <= end; })
        .reduce((s, r) => s + r.amount_cents, 0);

      const exp = (expenses || [])
        .filter(e => { const d = new Date(e.expense_date); return d >= start && d <= end; })
        .reduce((s, e) => s + e.amount_cents, 0);

      months.push({ name: label, Receita: rev / 100, Despesa: exp / 100 });
    }
    return months;
  }, [revenues, expenses]);

  // Últimas atividades
  const recentActivities = useMemo(() => {
    const activities: { type: string; text: string; date: string; icon: string }[] = [];

    (bookings || []).slice(0, 3).forEach(b => {
      activities.push({
        type: "reserva",
        text: `Reserva: ${b.area_name || "Área comum"}`,
        date: b.created_at,
        icon: "📅"
      });
    });

    (charges || []).slice(0, 3).forEach(c => {
      activities.push({
        type: "boleto",
        text: `Boleto: ${c.description}`,
        date: c.created_at,
        icon: "💰"
      });
    });

    (incidents || []).slice(0, 3).forEach(i => {
      activities.push({
        type: "chamado",
        text: `Chamado: ${i.title}`,
        date: i.created_at || "",
        icon: "🔧"
      });
    });

    (announcements || []).slice(0, 2).forEach(a => {
      activities.push({
        type: "comunicado",
        text: `Comunicado: ${a.title}`,
        date: a.created_at || "",
        icon: "📢"
      });
    });

    return activities
      .filter(a => a.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 6);
  }, [bookings, charges, incidents, announcements]);

  // Manutenções pendentes (ocorrências abertas/em andamento)
  const manutencoes = useMemo(() => {
    return (incidents || [])
      .filter(i => i.status === "open" || i.status === "in_progress")
      .slice(0, 10);
  }, [incidents]);

  const statusLabel = (s: string | null) => {
    if (s === "open") return <Badge variant="destructive">Aberto</Badge>;
    if (s === "in_progress") return <Badge className="bg-amber-500 text-white hover:bg-amber-600">Em andamento</Badge>;
    return <Badge variant="secondary">{s}</Badge>;
  };

  const priorityLabel = (p: string | null) => {
    if (p === "high" || p === "urgent") return <Badge className="bg-red-500 text-white hover:bg-red-600">Alta</Badge>;
    if (p === "medium") return <Badge className="bg-amber-400 text-white hover:bg-amber-500">Média</Badge>;
    return <Badge className="bg-green-500 text-white hover:bg-green-600">Baixa</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {condominium?.name || "Dashboard"}
        </h1>
        <p className="text-sm text-muted-foreground">
          Olá, {profile?.full_name || "Síndico"}! Aqui está o resumo do seu condomínio.
        </p>
      </div>

      {/* 4 Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Saldo em Caixa */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/financeiro")}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Saldo em Caixa</span>
              <DollarSign className="h-4 w-4 text-emerald-500" />
            </div>
            <p className={`text-2xl font-bold ${saldo >= 0 ? "text-emerald-600" : "text-red-600"}`}>
              {formatCurrency(saldo)}
            </p>
            <div className="flex items-center gap-1 mt-1">
              {saldo >= 0
                ? <TrendingUp className="h-3 w-3 text-emerald-500" />
                : <TrendingDown className="h-3 w-3 text-red-500" />}
              <span className="text-xs text-muted-foreground">Receitas - Despesas</span>
            </div>
          </CardContent>
        </Card>

        {/* Inadimplência */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/financeiro")}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Inadimplência</span>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-600">{inadimplencia.pct}%</p>
            <p className="text-xs text-muted-foreground mt-1">{inadimplencia.count} cobranças vencidas</p>
          </CardContent>
        </Card>

        {/* Chamados Abertos */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/ocorrencias")}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Chamados Abertos</span>
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-foreground">{chamados.total}</p>
              {chamados.urgent > 0 && (
                <Badge className="bg-red-500 text-white text-[10px] hover:bg-red-600">
                  {chamados.urgent} Urgente{chamados.urgent > 1 ? "s" : ""}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Abertos e em andamento</p>
          </CardContent>
        </Card>

        {/* Próximas Assembleias */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate("/reunioes")}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Próximas Assembleias</span>
              <CalendarClock className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-foreground">{proximasAssembleias.count}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {proximasAssembleias.proxima ? `Próxima: ${proximasAssembleias.proxima}` : "Nenhuma agendada"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Middle row: Chart + Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Cash Flow Chart */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Fluxo de Caixa — Últimos 6 Meses</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="name" className="text-xs" />
                <YAxis className="text-xs" tickFormatter={(v) => `R$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value * 100)}
                  labelClassName="font-medium"
                />
                <Legend />
                <Line type="monotone" dataKey="Receita" stroke="hsl(142, 71%, 45%)" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Despesa" stroke="hsl(0, 84%, 60%)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Recent Activities + Action Buttons */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Últimas Atividades</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3 max-h-[180px] overflow-y-auto">
              {recentActivities.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">Nenhuma atividade recente</p>
              )}
              {recentActivities.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-base leading-none mt-0.5">{a.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-foreground">{a.text}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(a.date), "dd/MM HH:mm", { locale: ptBR })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2 border-t">
              <Button
                variant="outline"
                className="w-full text-sm"
                onClick={() => navigate("/financeiro")}
              >
                <FileText className="h-4 w-4 mr-1.5" />
                Segunda Via
              </Button>
              <PublicarAvisoModal>
                <Button variant="outline" className="w-full text-sm">
                  <Bell className="h-4 w-4 mr-1.5" />
                  Comunicado
                </Button>
              </PublicarAvisoModal>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Manutenções Pendentes</CardTitle>
            <Button variant="link" size="sm" onClick={() => navigate("/ocorrencias")} className="text-xs">
              Ver todas →
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {manutencoes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Nenhuma manutenção pendente 🎉</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">ID</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead className="hidden md:table-cell">Local</TableHead>
                  <TableHead className="hidden sm:table-cell">Data Abertura</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Prioridade</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {manutencoes.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer" onClick={() => navigate("/ocorrencias")}>
                    <TableCell className="font-mono text-xs">
                      #{m.id.substring(0, 6).toUpperCase()}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{m.title}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {m.unit?.number ? `Unidade ${m.unit.number}` : "—"}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-muted-foreground">
                      {m.created_at ? format(new Date(m.created_at), "dd/MM/yyyy") : "—"}
                    </TableCell>
                    <TableCell>{statusLabel(m.status)}</TableCell>
                    <TableCell>{priorityLabel(m.priority)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <BtnSair />
    </div>
  );
};

export default DashboardSindico;
